<template>
  <div id="conexaoCaixaVinculo">
    <card-section-sem-permissao v-if="semPermissao" />

    <div v-else-if="carregandoInicial" class="text-center">
      <q-spinner color="blue-7" size="3em" />
    </div>

    <div v-else>
      <!-- Erro persistente: sem q-banner (nenhum painel vizinho usa) e sem o codigo
           na superficie. O codigo vive no "Detalhes", que o suporte pede por telefone. -->
      <div v-if="erro" class="row q-mb-md">
        <div class="col-12">
          <div class="text-negative">{{ erro.mensagem }}</div>
          <div class="q-mt-xs">
            <q-btn
              v-if="erro.acao === 'verificar'"
              flat dense no-caps
              color="primary"
              label="Tentar novamente"
              @click="verificarAgora"
            />
            <q-btn
              v-if="erro.acao === 'desconectar'"
              flat dense no-caps
              color="negative"
              label="Desconectar"
              @click="desconectarPorErro"
            />
            <q-btn flat dense no-caps color="grey-7" label="Dispensar" @click="limparErroVinculoConexaoCaixa" />
          </div>
          <q-expansion-item
            dense dense-toggle
            label="Detalhes"
            header-class="text-grey-6 text-caption"
            class="q-mt-xs"
          >
            <div class="text-caption text-grey-6">{{ erro.codigo }}</div>
          </q-expansion-item>
        </div>
      </div>

      <ConexaoCaixaVinculoAtivacao
        v-if="estado === 'sem_vinculo' || estado === 'revogado' || estado === 'aguardando_agente'"
        @atualizado="reavaliar"
        @recomecar="dialogRecomecar = true"
      />

      <ConexaoCaixaVinculoAguardandoLogin
        v-else-if="estado === 'aguardando_login'"
        @verificar="verificarAgora"
        @recomecar="dialogRecomecar = true"
      />

      <ConexaoCaixaVinculoPerfis
        v-else-if="estado === 'aguardando_perfil'"
        @atualizado="reavaliar"
        @desconectar="desconectarPorErro"
        @recomecar="dialogRecomecar = true"
      />

      <ConexaoCaixaVinculoAtivo
        v-else-if="estado === 'ativo'"
        @atualizado="reavaliar"
      />

      <!-- Estado que a UI nao conhece: mensagem generica. O valor cru so no "Detalhes",
           porque o nome do estado e justamente o tipo de pista que nao vai para a tela. -->
      <div v-else>
        <div class="text-grey-7">Não foi possível exibir a situação da integração.</div>
        <div class="q-mt-xs">
          <q-btn flat dense no-caps color="primary" label="Tentar novamente" @click="verificarAgora" />
        </div>
        <q-expansion-item
          dense dense-toggle
          label="Detalhes"
          header-class="text-grey-6 text-caption"
          class="q-mt-xs"
        >
          <div class="text-caption text-grey-6">{{ estado }}</div>
        </q-expansion-item>
      </div>

      <!-- Saida do meio do fluxo. Sem isto, quem comecou e travou (instalou na maquina
           errada, desistiu) fica olhando uma tela que espera algo que nao vem. O backend
           aceita desconectar em qualquer estado, nao so no ativo. -->
      <q-dialog v-model="dialogRecomecar" persistent>
        <q-card style="min-width: 320px; max-width: 420px">
          <q-card-section>
            <div class="text-subtitle1">Começar de novo</div>
            <div class="text-body2 q-mt-sm">
              A configuração em andamento será descartada. Você poderá recomeçar neste ou
              em outro computador.
            </div>
          </q-card-section>

          <q-card-actions align="right">
            <q-btn flat no-caps label="Cancelar" color="grey-8" :disable="recomecando" @click="dialogRecomecar = false" />
            <q-btn
              no-caps
              label="Começar de novo"
              color="negative"
              :loading="recomecando"
              :disable="recomecando"
              @click="confirmarRecomeco"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ConexaoCaixaVinculoAtivacao from "@/components/ConexaoCaixa/ConexaoCaixaVinculoAtivacao.vue";
import ConexaoCaixaVinculoAguardandoLogin from "@/components/ConexaoCaixa/ConexaoCaixaVinculoAguardandoLogin.vue";
import ConexaoCaixaVinculoPerfis from "@/components/ConexaoCaixa/ConexaoCaixaVinculoPerfis.vue";
import ConexaoCaixaVinculoAtivo from "@/components/ConexaoCaixa/ConexaoCaixaVinculoAtivo.vue";

export default {
  name: "ConexaoCaixaVinculo",

  components: {
    ConexaoCaixaVinculoAtivacao,
    ConexaoCaixaVinculoAguardandoLogin,
    ConexaoCaixaVinculoPerfis,
    ConexaoCaixaVinculoAtivo,
  },

  data() {
    return {
      carregandoInicial: true,
      semPermissao: false,
      dialogRecomecar: false,
      recomecando: false,
    };
  },

  computed: {
    ...mapGetters(["conexaoCaixaVinculoEstado", "conexaoCaixaVinculoErro"]),

    estado() { return this.conexaoCaixaVinculoEstado; },
    erro()   { return this.conexaoCaixaVinculoErro; },
  },

  watch: {
    estado: {
      immediate: false,
      handler() { this.sincronizarPolling(); },
    },
  },

  async mounted() {
    await this.fetchConexaoCaixaConfig();
    const res = await this.fetchStatusConexaoCaixa();

    // 403 = usuario sem ConexaoCaixas.gerenciar. Mesmo tratamento dos demais paineis
    // de Integracoes: tela "Sem Permissao", nunca um assistente meio quebrado.
    if (res && !res.ok && res.erro && res.erro.status === 403) {
      this.semPermissao = true;
      this.limparErroVinculoConexaoCaixa();
    }

    this.carregandoInicial = false;
    if (!this.semPermissao) this.sincronizarPolling();
  },

  beforeUnmount() {
    this.pararPollingVinculoConexaoCaixa();
  },

  methods: {
    ...mapActions([
      "fetchStatusConexaoCaixa",
      "fetchConexaoCaixaConfig",
      "iniciarPollingVinculoConexaoCaixa",
      "pararPollingVinculoConexaoCaixa",
      "desconectarConexaoCaixa",
      "limparErroVinculoConexaoCaixa",
    ]),

    sincronizarPolling() {
      if (this.estado === "aguardando_agente" || this.estado === "aguardando_login") {
        this.iniciarPollingVinculoConexaoCaixa();
      } else {
        this.pararPollingVinculoConexaoCaixa();
      }
    },

    async reavaliar() {
      await this.fetchStatusConexaoCaixa();
      this.sincronizarPolling();
    },

    async verificarAgora() {
      this.limparErroVinculoConexaoCaixa();
      await this.reavaliar();
    },

    // Recomecar = mesmo desconectar do estado ativo, disponivel no meio do fluxo.
    async confirmarRecomeco() {
      this.recomecando = true;
      const res = await this.desconectarConexaoCaixa();
      this.recomecando = false;
      this.dialogRecomecar = false;

      if (!res.ok) {
        this.$q.notify({ message: res.erro.mensagem, color: "red", position: "top-right" });
        return;
      }

      this.$q.notify({
        message: "Configuração descartada. Você pode começar de novo.",
        color: "orange",
        position: "top-right",
      });
      await this.reavaliar();
    },

    async desconectarPorErro() {
      const res = await this.desconectarConexaoCaixa();
      if (!res.ok) {
        this.$q.notify({ message: res.erro.mensagem, color: "red", position: "top-right" });
        return;
      }
      await this.reavaliar();
    },
  },
};
</script>
